import { z } from 'zod'
import { paginatedSchema } from './common.ts'

/** Pricing inputs the client needs to quote a top-up before submitting it. */
export const feeRateSchema = z.object({
  usdIdrRate: z.number().positive(),
  platformFeePercent: z.number().min(0),
  minTopupUsd: z.number().int().positive(),
  maxTopupUsd: z.number().int().positive(),
})

export type FeeRate = z.infer<typeof feeRateSchema>

export const topupQuoteSchema = z.object({
  usdAmount: z.number(),
  usdIdrRate: z.number(),
  platformFeePercent: z.number(),
  idrAmount: z.number().int(),
  platformFeeIdr: z.number().int(),
  totalIdr: z.number().int(),
})

export type TopupQuote = z.infer<typeof topupQuoteSchema>

/** V1 only ever accepted whole dollars (`parseInt`); V2 keeps that rule explicit. */
export const createTopupRequestSchema = z.object({
  usdAmount: z.number().int().positive(),
})

export type CreateTopupRequest = z.infer<typeof createTopupRequestSchema>

export const topupStatusSchema = z.enum(['pending', 'paid', 'expired', 'failed', 'cancelled'])

export type TopupStatus = z.infer<typeof topupStatusSchema>

export const topupSchema = z.object({
  id: z.string(),
  status: topupStatusSchema,
  usdAmount: z.number(),
  idrAmount: z.number().int(),
  platformFeeIdr: z.number().int(),
  totalIdr: z.number().int(),
  /**
   * Raw QRIS payload the client renders as a QR code. Null once it is no longer
   * payable — and also while the invoice is still open but the gateway's
   * short-lived dynamic QR has lapsed, in which case `paymentUrl` is the way on.
   */
  qrString: z.string().nullable(),
  /** The gateway's hosted checkout page, which always shows a fresh QR. Null once not payable. */
  paymentUrl: z.string().nullable(),
  expiresAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
})

export type Topup = z.infer<typeof topupSchema>

export const topupPageSchema = paginatedSchema(topupSchema)

export type TopupPage = z.infer<typeof topupPageSchema>
